import {
  Button,
  Grid,
  Typography,
  FormControl,
  FormControlLabel,
  FormLabel,
  Checkbox,
  TextField,
  FormHelperText,
} from '@mui/material';
import React, { FC, useState } from 'react';
import { SHOW_FEEDBACK, useAddSupplierNoQuoteMutation } from '@syncfab/machine';
import { useAppDispatch } from '../../../../application/hooks';

interface NoQuoteProps {
  rfqId: string;
  cancel: () => void;
  complete: () => void;
}

const labels = [
  'Manufacturing Capability Issue',
  'Certification Capability Issue',
  'No Available Capacity Currently',
  'Material Sourcing Issue',
  'Lead Time Too Short',
  'Lead Time Too Long',
  'Quantity Too Small',
  'Quantity Too Large',
  'Target Price Issue',
  'File or Technical Issue',
];
const labelToStateMap = {
  'Manufacturing Capability Issue': 'manufacturing',
  'Certification Capability Issue': 'certification',
  'No Available Capacity Currently': 'capacity',
  'Material Sourcing Issue': 'material',
  'Lead Time Too Short': 'short',
  'Lead Time Too Long': 'long',
  'Quantity Too Small': 'small',
  'Quantity Too Large': 'large',
  'Target Price Issue': 'target',
  'File or Technical Issue': 'file',
} as const;
const stateToLabelMap = Object.fromEntries(
  Object.entries(labelToStateMap).map(e => [e[1], e[0]])
);

export const NoQuote: FC<NoQuoteProps> = ({ cancel, complete, rfqId }) => {
  const dispatch = useAppDispatch();
  const [showNoSelectionError, setShowNoSelectionError] = useState(false);
  const [otherText, setOtherText] = useState('');
  const [checkState, setCheckState] = useState({
    manufacturing: false,
    certification: false,
    capacity: false,
    material: false,
    short: false,
    long: false,
    small: false,
    large: false,
    target: false,
    file: false,
    other: false,
  });
  const [noQuote] = useAddSupplierNoQuoteMutation();

  const onSubmit = async () => {
    let submitText = '';
    for (const labelValue of Object.values(labelToStateMap)) {
      if (checkState[labelValue]) {
        if (submitText != '') submitText = submitText + ', ';
        submitText += stateToLabelMap[labelValue];
      }
    }
    if (checkState.other) {
      if (submitText != '') submitText += ', ';
      submitText += otherText;
    }
    if (submitText != '') {
      try {
        await noQuote({
          rfqId,
          noQuoteReason: submitText,
        }).unwrap();
        complete();
        dispatch(SHOW_FEEDBACK('Your no quote was recorded.'));
      } catch (error) {
        dispatch(SHOW_FEEDBACK(error.data));
      }
    } else {
      setShowNoSelectionError(true);
    }
  };

  return (
    <>
      <Typography
        style={{
          fontSize: 22,
          fontWeight: 600,
          paddingTop: '8px',
          paddingBottom: '16px',
        }}
      >
        Decline Quote
      </Typography>
      <FormControl variant="standard">
        <FormLabel focused={false} style={{ paddingTop: 16, paddingBottom: 24 }}>
          Kindly share your feedback regarding the reasons for declining this
          quote, as it will assist us in improving our future quotes to you.
        </FormLabel>
        <>
          {labels.map(l => {
            return (
              <FormControlLabel
                key={`${l}Checkbox`}
                control={
                  <Checkbox
                    checked={checkState[labelToStateMap[l]]}
                    onChange={() => {
                      // Hide the error if it's showing and a box is now selected
                      if (
                        !checkState[labelToStateMap[l]] &&
                        showNoSelectionError
                      )
                        setShowNoSelectionError(false);
                      setCheckState({
                        ...checkState,
                        [labelToStateMap[l]]: !checkState[labelToStateMap[l]],
                      });
                    }}
                    name={l}
                  />
                }
                label={l}
              />
            );
          })}
          <FormControlLabel
            control={
              <Checkbox
                checked={checkState.other}
                onChange={() => {
                  if (!checkState.other && showNoSelectionError)
                    setShowNoSelectionError(false);
                  setCheckState({
                    ...checkState,
                    other: !checkState.other,
                  });
                }}
                name="other"
              />
            }
            label="Other"
          />
          <TextField
            value={otherText}
            onChange={e => {
              setOtherText(e.target.value);
              if (!checkState.other)
                setCheckState({ ...checkState, other: true });
            }}
            multiline
            minRows={2}
            variant="outlined"
          />
          {showNoSelectionError && (
            <FormHelperText error>
              Please select at least one reason.
            </FormHelperText>
          )}
        </>
      </FormControl>
      <Grid
        container
        justifyContent="flex-end"
        style={{ paddingTop: '16px', paddingBottom: '16px' }}
      >
        <Button
          variant="text"
          style={{ color: '#40B4BF' }}
          onClick={() => cancel()}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          style={{
            backgroundColor: '#40B4BF',
            color: '#FFFFFF',
            marginLeft: '16px',
          }}
          onClick={onSubmit}
        >
          No Quote
        </Button>
      </Grid>
    </>
  );
};
